import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";

export type SecurityEventType = 
  | 'suspended_access_attempt'
  | 'suspended_page_view'
  | 'suspended_sign_out'
  // Client error tracking types
  | 'client_error_boundary'
  | 'client_chunk_load_error'
  | 'client_skeleton_timeout'
  | 'client_query_error'
  | 'client_mutation_error'
  | 'client_network_timeout'
  | 'client_unhandled_error'
  | 'client_promise_rejection'
  // Pipeline self-test (never counted as a real error)
  | 'client_diagnostic_test';

// Simple rate limiting to avoid flooding the database
const recentErrors = new Map<string, number>();
const RATE_LIMIT_MS = 5000; // 5 seconds between identical errors

const shouldLogError = (errorKey: string): boolean => {
  const now = Date.now();
  const lastLogged = recentErrors.get(errorKey);
  
  if (lastLogged && now - lastLogged < RATE_LIMIT_MS) {
    return false;
  }
  
  recentErrors.set(errorKey, now);
  
  // Clean up old entries periodically
  if (recentErrors.size > 100) {
    for (const [key, time] of recentErrors) {
      if (now - time > RATE_LIMIT_MS * 2) {
        recentErrors.delete(key);
      }
    }
  }
  
  return true;
};

/**
 * Cached user id.
 * Global error handlers must never await a network auth call before logging —
 * during a hard crash or an offline state that promise can hang and the error
 * is lost. We keep the id in memory and refresh it from auth state changes.
 */
let cachedUserId: string | null = null;

export const getCachedUserId = (): string | null => cachedUserId;

// Prime the cache (non-blocking) and keep it in sync.
supabase.auth.getSession()
  .then(({ data }) => { cachedUserId = data.session?.user?.id ?? null; })
  .catch(() => { /* ignore */ });

supabase.auth.onAuthStateChange((_event, session) => {
  cachedUserId = session?.user?.id ?? null;
});

/** Health of the logging pipeline itself, surfaced in the admin dashboard. */
interface LoggerHealth {
  consecutiveFailures: number;
  lastFailureMessage: string | null;
  lastSuccessAt: string | null;
  queuedCount: number;
}

const health: LoggerHealth = {
  consecutiveFailures: 0,
  lastFailureMessage: null,
  lastSuccessAt: null,
  queuedCount: 0,
};

export const getLoggerHealth = (): LoggerHealth => ({ ...health, queuedCount: pendingRows.length });

type EventRow = {
  user_id: string | null;
  event_type: string;
  description: string;
  metadata: Json;
  user_agent: string;
};

// Rows whose insert failed: retried once on the next log attempt so a transient
// network blip does not silently swallow the very error we need to see.
const pendingRows: EventRow[] = [];
const MAX_PENDING = 20;

const insertRows = async (rows: EventRow[]): Promise<boolean> => {
  const { error } = await supabase.from("security_events").insert(rows);
  if (error) {
    health.consecutiveFailures += 1;
    health.lastFailureMessage = error.message;
    console.error('Failed to log security event:', error.message);
    return false;
  }
  health.consecutiveFailures = 0;
  health.lastFailureMessage = null;
  health.lastSuccessAt = new Date().toISOString();
  return true;
};

export const logSecurityEvent = async (
  eventType: SecurityEventType,
  userId: string | null,
  description: string,
  metadata?: Record<string, string | number | boolean | null>
): Promise<void> => {
  try {
    // Rate limit identical errors
    const errorKey = `${eventType}:${description}:${userId || 'anon'}`;
    if (!shouldLogError(errorKey)) {
      return;
    }

    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown';

    const row: EventRow = {
      user_id: userId ?? cachedUserId,
      event_type: eventType,
      description: description,
      metadata: (metadata || {}) as Json,
      user_agent: userAgent,
    };

    // Flush anything that failed previously alongside this event.
    const batch = pendingRows.splice(0, pendingRows.length);
    batch.push(row);

    const ok = await insertRows(batch);
    if (!ok) {
      pendingRows.push(...batch.slice(-MAX_PENDING));
    }
  } catch (error) {
    // Silently fail - we don't want security logging to break the app
    health.consecutiveFailures += 1;
    health.lastFailureMessage = error instanceof Error ? error.message : String(error);
    console.error('Failed to log security event:', error);
  }
};

/**
 * End-to-end self-test of the error pipeline.
 * Writes a clearly-labeled synthetic event through the same insert path a real
 * error uses, then reads it back to prove capture + visibility both work.
 */
export const runErrorPipelineDiagnostic = async (): Promise<{
  ok: boolean;
  roundTripMs: number;
  message: string;
}> => {
  const started = Date.now();
  const marker = `diagnostic-${started}-${Math.random().toString(36).slice(2, 8)}`;

  try {
    const { error: insertError } = await supabase.from("security_events").insert([{
      user_id: cachedUserId,
      event_type: 'client_diagnostic_test',
      description: `Pipeline self-test ${marker}`,
      metadata: { diagnostic: true, marker, page_url: typeof window !== 'undefined' ? window.location.pathname : 'unknown' } as Json,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
    }]);

    if (insertError) {
      health.consecutiveFailures += 1;
      health.lastFailureMessage = insertError.message;
      return { ok: false, roundTripMs: Date.now() - started, message: `Write failed: ${insertError.message}` };
    }

    const { data, error: readError } = await supabase
      .from("security_events")
      .select('id, created_at')
      .eq('event_type', 'client_diagnostic_test')
      .ilike('description', `%${marker}%`)
      .limit(1);

    if (readError) {
      return { ok: false, roundTripMs: Date.now() - started, message: `Read-back failed: ${readError.message}` };
    }

    if (!data || data.length === 0) {
      return { ok: false, roundTripMs: Date.now() - started, message: 'Event was written but could not be read back.' };
    }

    health.consecutiveFailures = 0;
    health.lastFailureMessage = null;
    health.lastSuccessAt = new Date().toISOString();
    return { ok: true, roundTripMs: Date.now() - started, message: 'Error pipeline is capturing and storing events.' };
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    return { ok: false, roundTripMs: Date.now() - started, message };
  }
};
